import React, { PropsWithChildren, useCallback, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { DropdownIndicatorProps } from 'react-select';
import { PortalWithState } from 'react-portal';
import AddAddressModal from './AddAddressModal';
import { StylesConfig } from 'react-select/dist/declarations/src/styles';
import { AsyncSelect } from '../ReactSelect';
import ReactSelectButton from '../ReactSelect/ReactSelectButton';
import { gql } from '@/modes/common/gql';
import { ResultOf } from 'gql.tada';

export const GET_ADDRESSES = gql(`
  query GetAddresses($where: JSON) {
    addresses(where: $where) {
      id
      street1
      street2
      city
      state
      zip
      zipExt
    }
  }
`);

export type AddressType = ResultOf<typeof GET_ADDRESSES>['addresses'][number];

type AddressPickerProps = {
  addressId?: string | null;
  onChange?: (address: AddressType | null) => void;
  placeholder?: string;
  allowCreate?: boolean;
  autoFocus?: boolean;
  isDisabled?: boolean;
};

export const addressToString = (address?: AddressType) => {
  const directions = ['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW'];

  if (!address) {
    return '';
  }

  const { street1, street2, city, state, zip, zipExt } = address;

  const formattedStreet = street1
    .split(' ')
    .map((el: string) => {
      if (directions.includes(el)) {
        return el;
      } else {
        return `${el[0]}${el.slice(1).toLowerCase()}`;
      }
    })
    .join(' ');

  const formattedStreet2 =
    street2 &&
    street2
      .split(' ')
      .map((el: string) => `${el[0]}${el.slice(1).toLowerCase()}`)
      .join(' ');

  const formattedCity = city
    .split(' ')
    .map((el: string) => `${el[0]}${el.slice(1).toLowerCase()}`)
    .join(' ');

  return `${formattedStreet},${
    formattedStreet2 && ` ${formattedStreet2},`
  } ${formattedCity}, ${state} ${zip}${zipExt && `-${zipExt}`}`;
};

const AddressPicker = ({
  addressId,
  onChange,
  allowCreate,
  placeholder,
  autoFocus,
  isDisabled,
}: AddressPickerProps): JSX.Element => {
  const client = useApolloClient();

  const [searchString, setSearchString] = useState<string>('');

  const { error: itemError, data: itemData } = useQuery<{
    addresses: AddressType[];
  }>(GET_ADDRESSES, {
    variables: {
      where: {
        id: addressId,
      },
    },
    skip: !addressId,
  });

  const selectedAddress: AddressType | undefined = itemData?.addresses[0];

  if (itemError) {
    throw itemError;
  }

  const loadOptionsFunc = async (inputValue: string) => {
    const res = await client.query({
      query: GET_ADDRESSES,
      fetchPolicy: 'network-only',
      variables: {
        where: {
          street1: {
            like: `%${inputValue.split(',', 1)[0].replace(/ /g, '%')}%`,
          },
        },
      },
    });

    if (res.data && res.data.addresses) {
      return res.data.addresses;
    }

    return [];
  };

  const onInputChange = useCallback(
    (inputValue: string, { action }: { action: string }) => {
      if (action === 'input-change') {
        setSearchString(inputValue);
      }
    },
    [setSearchString]
  );

  const customStyles: StylesConfig<any, false, any> = {
    indicatorsContainer: (provided, state) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
    indicatorSeparator: (provided, state) => ({
      ...provided,
      visibility: allowCreate ? 'visible' : 'hidden',
    }),
    control: (provided, state) => ({
      ...provided,
      borderRadius: '99999px',
    }),
  };

  return (
    <PortalWithState closeOnEsc>
      {({ openPortal, closePortal, portal, isOpen }) => {
        return (
          <>
            <AsyncSelect
              cacheOptions
              autoFocus={autoFocus}
              components={{
                DropdownIndicator: (
                  props: PropsWithChildren<
                    DropdownIndicatorProps<AddressType, false>
                  >
                ) => {
                  return allowCreate && !isDisabled ? (
                    <ReactSelectButton
                      onClick={openPortal}
                      title="Add new address"
                    />
                  ) : null;
                },
              }}
              value={selectedAddress}
              loadOptions={loadOptionsFunc}
              isDisabled={isDisabled}
              isClearable
              noOptionsMessage={() => null}
              menuPlacement="auto"
              onChange={(selected: AddressType | null) => {
                if (!selected) {
                  onChange?.(null);
                } else {
                  onChange?.(selected);
                }
              }}
              placeholder={placeholder ?? 'Enter address...'}
              onInputChange={onInputChange}
              getOptionLabel={(address) => addressToString(address)}
              getOptionValue={(address) => address.id}
              styles={customStyles}
            />
            {portal(
              <AddAddressModal
                userInput={searchString}
                onSave={onChange}
                onClose={closePortal}
              />
            )}
          </>
        );
      }}
    </PortalWithState>
  );
};

export default AddressPicker;
